/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { useAtom } from "jotai";
import globalState from "../store";
import { button, containerCentered, input } from "./styles";
import { searchIndustries } from "../utils/server/industry";
import { IIndustry } from "../types/types";
import formatLandingPage, {
  getTestimonialsWithDraggableId,
} from "../utils/helpers/formatLandingPage";
import { useDebounce } from "use-debounce";

const NewSite: React.FC = () => {
  const [globalStateAtom, setGlobalState] = useAtom(globalState);
  const [companyName, setCompanyName] = useState("");
  const [industry, setIndustry] = useState(0);
  const [filteredIndustries, setFilteredIndustries] = useState<IIndustry[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch] = useDebounce(searchTerm, 300);
  const [loadingIndustries, setLoadingIndustries] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Server-side search for industries; avoid hitting backend on empty query
  useEffect(() => {
    let cancelled = false;
    async function run() {
      const q = debouncedSearch.trim();
      if (!q) {
        setFilteredIndustries([]);
        return;
      }
      setLoadingIndustries(true);
      try {
        const results = await searchIndustries(q, { limit: 20 });
        if (!cancelled) setFilteredIndustries(results);
      } catch (e) {
        if (!cancelled) setFilteredIndustries([]);
      } finally {
        if (!cancelled) setLoadingIndustries(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch]);

  // Click-away handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  // No initial fetch; results appear as user types

  const handleSelectIndustry = (id: number, name: string) => {
    setIndustry(id);
    setSearchTerm(name);
    setDropdownVisible(false);
  };

  // Poll task status
  const pollTaskStatus = (taskId: string) => {
    const timeout =
      Number(process.env.REACT_APP_SITE_CREATION_TASK_TIMEOUT) || 180000;
    const startTime = Date.now();

    const interval = setInterval(async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_SERVER_URL}/api/site-creation/${taskId}/status/`
        );
        if (data.status === "completed") {
          clearInterval(interval);
          setLoading(false);
          const slug = data.landing_page_slug;
          if (slug) {
            await refetchAllData(slug);
            history.push(`/logo/${slug}`);
          } else {
            setError(true);
            setErrorMessage("Error creating site.");
          }
        } else if (data.status === "failed") {
          setError(true);
          setErrorMessage(data.error || "Task failed.");
          clearInterval(interval);
          setLoading(false);
        } else if (Date.now() - startTime > timeout) {
          setError(true);
          setErrorMessage("Task timed out. Please try again.");
          clearInterval(interval);
          setLoading(false);
        }
      } catch (err) {
        console.error(err);
        setError(true);
        setErrorMessage("Error polling task status.");
        clearInterval(interval);
        setLoading(false);
      }
    }, 5000); // Poll every 5 seconds
  };

  const handleMakeNew = async () => {
    setErrorMessage("");
    localStorage.setItem("globalState", "");

    try {
      setError(false);
      setErrorMessage("");
      localStorage.setItem("globalState", "");
      setLoading(true);

      const data = {
        companyName,
        industry,
      };

      const createSiteResponse = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/landing-pages/`,
        data
      );

      const taskId = createSiteResponse.data.task_id;
      if (taskId) {
        pollTaskStatus(taskId);
      } else {
        setError(true);
        setErrorMessage("Error creating site.");
        setLoading(false);
      }
    } catch (e: unknown) {
      const errorMessage = (e as any)?.response?.data?.message;

      setError(true);
      setErrorMessage(errorMessage);
      setLoading(false);
    }
  };

  const getFormattedLandingPageData = async (slug: string) => {
    try {
      const fetchedDataResponse = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/getLandingPage/${slug}`
      );

      const fetchedData = fetchedDataResponse.data.message;

      const formattedData = await formatLandingPage(fetchedData);

      formattedData.service_areas.sort((a: any, b: any) => a.order - b.order);
      formattedData.service_area_array.sort((a: any, b: any) => {
        a[a.length - 1] - b[b.length - 1];
      });

      formattedData.testimonials = getTestimonialsWithDraggableId(
        formattedData.testimonials
      );

      return formattedData;
    } catch (e: unknown) {
      const msg = (e as any)?.message || "Unknown error";
      alert(`Error getting landing page data for slug '${slug}': ${msg}`);
      return null;
    }
  };

  const handleUpdateGlobalStore = (formattedData: any) => {
    setGlobalState({
      ...formattedData,
      multipage: "multipage" in formattedData ? formattedData.multipage : true,
    });
  };

  async function fetchAllData(slug: string) {
    const formattedData = await getFormattedLandingPageData(slug);
    handleUpdateGlobalStore(formattedData);
  }

  async function refetchAllData(slug: string) {
    localStorage.setItem("globalState", "");
    await fetchAllData(slug);
  }

  return (
    <>
      <div className={containerCentered}>
        {errorMessage !== "" ? <div>{errorMessage}</div> : ""}
        {loading && !error ? (
          <img src="/prophone-load.gif" width="50" className="-mt-16" />
        ) : (
          <img src="prophone_favicon_square.png" width="50" className="mb-2" />
        )}
        <h2 className="text-3xl">
          Creat{loading && !error ? "ing" : "e"} a Site
        </h2>
        <input
          onChange={(e) => setCompanyName(e.target.value)}
          placeholder="Company Name"
          className={input}
        />
        <div style={{ position: "relative" }} ref={dropdownRef}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setDropdownVisible(true)}
            placeholder="Search for an industry"
            className={input}
          />
          {dropdownVisible && (
            <ul
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                backgroundColor: "white",
                border: "1px solid #ccc",
                zIndex: 1000,
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              {loadingIndustries && (
                <li style={{ padding: "10px", color: "#6b7280" }}>Searching…</li>
              )}
              {!loadingIndustries && filteredIndustries.length === 0 && debouncedSearch.trim() && (
                <li style={{ padding: "10px", color: "#6b7280" }}>No industries found</li>
              )}
              {filteredIndustries.map((i) => (
                <li
                  key={i.id}
                  style={{
                    padding: "10px",
                    cursor: "pointer",
                    borderBottom: "1px solid #eee",
                  }}
                  onClick={() => handleSelectIndustry(i.id, i.industry_name)}
                >
                  {i.industry_name}
                </li>
              ))}
            </ul>
          )}
        </div>
        {!loading && (
          <button onClick={handleMakeNew} className={button} disabled={loading}>
            Create
          </button>
        )}
        {error && (
          <p className="text-red-500">
            There was an error building this website. Please try again after
            yelling at Dylan.
          </p>
        )}
      </div>
    </>
  );
};

export default NewSite;
